<script setup lang="ts" generic="T extends Privilege.Map">
import { api } from "@/api";
import { PV } from "@/widgets";
import { type Component, computed, ref, watch } from "vue";
import KeyIcon from "~icons/bxs/key";
import { defaultResetFn } from "../../common/defaults.common";
import { type Privilege, privileges } from "../../common/privilege.common";
import { defineActions, defineState } from "../common";
import { FormInputSelect } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface OptionType {
    label: string;
    value: string;
}

type ModelType = Record<string, boolean>;
interface PropsType extends BaseProps<ModelType> {
    privilegeMap: T;
    options?: (() => Promise<OptionType[]>) | OptionType[] | string;
    searchable?: boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
    searchable: false,
});

const slots = defineSlots<{
    [K in keyof T as K]: () => Component;
}>();

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const base = ref<string>();
const baseMap = computed(() => privileges.create(props.privilegeMap));
const toggled = computed(() => {
    if (model.value === undefined) return 0;
    return Object.keys(model.value).reduce((a, k) => a + (model.value[k] ? 1 : 0), 0);
});
const extended = computed(() => {
    return Object.getOwnPropertyNames(slots).length > 0;
});

watch(base, async (v) => {
    if (v === undefined) {
        Object.keys(model.value!).forEach((k) => (model.value![k] = false));
        return;
    }

    const data = await api.privilege.formHooks.obtainTemplate(v);
    Object.keys(baseMap.value).forEach((k) => (model.value![k] = false));
    Object.values(data ?? []).forEach((k) => (model.value![k] = true));
});

function batchSet(value: boolean) {
    if (value === false) base.value = undefined;
    Object.keys(baseMap.value).forEach((k) => (model.value![k] = value));
}
</script>

<template>
    <FormPanel :param="props" toggleable>
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <KeyIcon class="ml-1" />
                <span class="font-semibold text-gray-600">
                    {{ props.label ?? "Pengaturan Hak Akses" }}
                </span>
                <PV.Badge severity="secondary" :value="toggled" />
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row mr-2">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div v-if="state.editMode" class="flex flex-col md:flex-row gap-2 items-end justify-center mb-4">
            <FormInputSelect
                v-if="props.options !== undefined"
                v-model="base"
                :attachReset="defaultResetFn"
                class="w-full md:w-auto md:grow"
                embedMode="embed"
                label="Template Hak Akses"
                :mode="state.nestMode"
                :options="props.options"
                :searchable="props.searchable"
            />
            <div class="w-full md:w-auto flex flex-row gap-2">
                <PV.Button
                    class="w-full md:w-auto bg-green-500! border-0! shadow-xs rounded-lg!"
                    label="Pilih Semua"
                    @click="() => batchSet(true)"
                />
                <PV.Button
                    class="w-full md:w-auto bg-red-500! border-0! shadow-xs rounded-lg!"
                    label="Hapus Semua"
                    @click="() => batchSet(false)"
                />
            </div>
        </div>

        <template v-for="(child, key) in props.privilegeMap">
            <div class="bg-slate-100 p-4 font-medium text-2xl">{{ child.name }}</div>
            <div v-if="!extended" class="grid grid-cols-1 xl:grid-cols-2 gap-4 p-4">
                <template v-for="access in child.privileges">
                    <div class="flex flex-row gap-4 mx-2">
                        <PV.ToggleSwitch
                            v-if="state.editMode"
                            v-model="model[`${key}.${access.name}`]"
                            class="min-w-10"
                            :inputId="`${key}.${access.name}`"
                        />
                        <PV.Checkbox
                            v-else
                            v-model="model[`${key}.${access.name}`]"
                            binary
                            disabled
                            :inputId="`${key}.${access.name}`"
                        />
                        <label class="font-medium" :for="`${key}.${access.name}`">
                            {{ access.name.toUpperCase() }} - {{ access.desc }} {{ child.name }}
                        </label>
                    </div>
                </template>
            </div>
            <div v-else>
                <template v-for="access in child.privileges">
                    <FormPanel collapsed :param="props" toggleable>
                        <template #header>
                            <div class="flex flex-row gap-4 mx-2 py-4">
                                <PV.ToggleSwitch
                                    v-if="state.editMode"
                                    v-model="model[`${key}.${access.name}`]"
                                    class="min-w-10"
                                    :inputId="`${key}.${access.name}`"
                                />
                                <PV.Checkbox
                                    v-else
                                    v-model="model[`${key}.${access.name}`]"
                                    binary
                                    disabled
                                    :inputId="`${key}.${access.name}`"
                                />
                                <label class="font-medium" :for="`${key}.${access.name}`">
                                    {{ access.name.toUpperCase() }} - {{ access.desc }} {{ child.name }}
                                </label>
                            </div>
                        </template>

                        <div class="flex flex-col gap-2">
                            <PV.Divider />
                            <template v-if="slots[key] !== undefined">
                                <div class="w-full">
                                    <slot :name="key" />
                                </div>
                            </template>
                        </div>
                    </FormPanel>
                </template>
            </div>
        </template>
    </FormPanel>
</template>
